import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { format, startOfMonth, endOfMonth, subMonths } from "date-fns";
import { de } from "date-fns/locale";
import { Loader2, Car, Camera, CreditCard, Search, Eye, ChevronLeft, ChevronRight, Calendar } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface OdometerEntry {
  id: string;
  employee_id: string;
  employee_name: string;
  vehicle_name: string;
  license_plate: string;
  reading_date: string;
  odometer_value: number;
  kilometers_this_month: number | null;
  photo_url: string | null;
  notes: string | null;
}

interface VehiclePhotoEntry {
  id: string;
  employee_id: string;
  employee_name: string;
  vehicle_name: string;
  license_plate: string;
  photo_type: string;
  photo_url: string;
  created_at: string;
  notes: string | null;
}

interface LicenseVerificationEntry {
  id: string;
  employee_id: string;
  employee_name: string;
  quarter: string;
  year: number;
  verification_date: string;
  photo_url: string;
  notes: string | null;
}

const VehicleUploadsOverviewPage = () => {
  const { user, userRole } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  
  const [odometerReadings, setOdometerReadings] = useState<OdometerEntry[]>([]);
  const [vehiclePhotos, setVehiclePhotos] = useState<VehiclePhotoEntry[]>([]);
  const [licenseVerifications, setLicenseVerifications] = useState<LicenseVerificationEntry[]>([]);

  const isAdmin = userRole === "admin";
  const isHRManager = userRole === "hr_manager";
  const canView = isAdmin || isHRManager;

  useEffect(() => {
    if (!canView) {
      navigate("/");
      return;
    }
    loadAllData();
  }, [canView, selectedMonth]);

  const loadAllData = async () => {
    if (!user) return;
    setLoading(true);

    try {
      const monthStart = format(startOfMonth(selectedMonth), "yyyy-MM-dd");
      const monthEnd = format(endOfMonth(selectedMonth), "yyyy-MM-dd");

      // Load odometer readings with employee and vehicle info
      const { data: odometerData } = await supabase
        .from("vehicle_odometer_readings")
        .select(`
          id, reading_date, odometer_value, kilometers_this_month, photo_url, notes, employee_id,
          vehicles!inner(name, license_plate),
          employees!inner(name)
        `)
        .gte("reading_date", monthStart)
        .lte("reading_date", monthEnd)
        .order("reading_date", { ascending: false });

      if (odometerData) {
        setOdometerReadings(odometerData.map((entry: any) => ({
          id: entry.id,
          employee_id: entry.employee_id,
          employee_name: entry.employees?.name || "Unbekannt",
          vehicle_name: entry.vehicles?.name || "Unbekannt",
          license_plate: entry.vehicles?.license_plate || "-",
          reading_date: entry.reading_date,
          odometer_value: entry.odometer_value,
          kilometers_this_month: entry.kilometers_this_month,
          photo_url: entry.photo_url,
          notes: entry.notes
        })));
      }

      // Load vehicle photos
      const { data: photosData } = await supabase
        .from("vehicle_photos")
        .select(`
          id, photo_type, photo_url, created_at, notes, employee_id,
          vehicles!inner(name, license_plate),
          employees!inner(name)
        `)
        .gte("created_at", monthStart)
        .lte("created_at", monthEnd + "T23:59:59")
        .order("created_at", { ascending: false });

      if (photosData) {
        setVehiclePhotos(photosData.map((entry: any) => ({
          id: entry.id,
          employee_id: entry.employee_id,
          employee_name: entry.employees?.name || "Unbekannt",
          vehicle_name: entry.vehicles?.name || "Unbekannt",
          license_plate: entry.vehicles?.license_plate || "-",
          photo_type: entry.photo_type,
          photo_url: entry.photo_url,
          created_at: entry.created_at,
          notes: entry.notes
        })));
      }

      // Load license verifications
      const { data: licenseData } = await supabase
        .from("driver_license_verifications")
        .select(`
          id, quarter, year, verification_date, photo_url, notes, employee_id,
          employees!inner(name)
        `)
        .gte("verification_date", monthStart)
        .lte("verification_date", monthEnd)
        .order("verification_date", { ascending: false });

      if (licenseData) {
        setLicenseVerifications(licenseData.map((entry: any) => ({
          id: entry.id,
          employee_id: entry.employee_id,
          employee_name: entry.employees?.name || "Unbekannt",
          quarter: entry.quarter,
          year: entry.year,
          verification_date: entry.verification_date,
          photo_url: entry.photo_url,
          notes: entry.notes
        })));
      }

    } catch (error) {
      console.error("Error loading data:", error);
    } finally {
      setLoading(false);
    }
  };

  const filterBySearch = <T extends { employee_name: string }>(items: T[]): T[] => {
    if (!searchTerm) return items;
    const term = searchTerm.toLowerCase();
    return items.filter(item => 
      item.employee_name.toLowerCase().includes(term)
    );
  };

  const getPhotoTypeBadge = (type: string) => {
    switch (type) {
      case "interior": return <Badge variant="secondary">Innenraum</Badge>;
      case "exterior": return <Badge variant="outline">Außen</Badge>;
      case "damage": return <Badge variant="destructive">Schaden</Badge>;
      default: return <Badge>{type}</Badge>;
    }
  };

  const navigateToPreviousMonth = () => {
    setSelectedMonth(prev => subMonths(prev, 1));
  };

  const navigateToNextMonth = () => {
    setSelectedMonth(prev => {
      const next = new Date(prev);
      next.setMonth(next.getMonth() + 1);
      return next;
    });
  };

  if (!canView) {
    return null;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  const filteredOdometer = filterBySearch(odometerReadings);
  const filteredPhotos = filterBySearch(vehiclePhotos);
  const filteredLicense = filterBySearch(licenseVerifications);

  return (
    <div className="container mx-auto px-2 sm:px-4 py-4 sm:py-8 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold">Fahrzeugdaten-Übersicht</h1>
          <p className="text-muted-foreground">Alle Uploads der Mitarbeiter (Tachostände, Fotos, Führerscheine)</p>
        </div>
      </div>

      {/* Month Navigation and Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
            <div className="flex items-center gap-2">
              <Button variant="outline" size="icon" onClick={navigateToPreviousMonth}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <div className="flex items-center gap-2 px-4 py-2 bg-muted rounded-md min-w-[180px] justify-center">
                <Calendar className="h-4 w-4" />
                <span className="font-medium">
                  {format(selectedMonth, "MMMM yyyy", { locale: de })}
                </span>
              </div>
              <Button variant="outline" size="icon" onClick={navigateToNextMonth}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Mitarbeiter suchen..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Car className="h-4 w-4" />
              Tachostände
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{odometerReadings.length}</div>
            <p className="text-xs text-muted-foreground">Uploads in diesem Monat</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Camera className="h-4 w-4" />
              Fahrzeugfotos
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{vehiclePhotos.length}</div>
            <p className="text-xs text-muted-foreground">Uploads in diesem Monat</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <CreditCard className="h-4 w-4" />
              Führerscheinprüfungen
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{licenseVerifications.length}</div>
            <p className="text-xs text-muted-foreground">Uploads in diesem Monat</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs with Tables */}
      <Tabs defaultValue="odometer" className="space-y-4">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="odometer" className="flex items-center gap-2">
            <Car className="h-4 w-4 hidden sm:block" />
            Tachostände
          </TabsTrigger>
          <TabsTrigger value="photos" className="flex items-center gap-2">
            <Camera className="h-4 w-4 hidden sm:block" />
            Fotos
          </TabsTrigger>
          <TabsTrigger value="license" className="flex items-center gap-2">
            <CreditCard className="h-4 w-4 hidden sm:block" />
            Führerscheine
          </TabsTrigger>
        </TabsList>

        {/* Odometer Tab */}
        <TabsContent value="odometer">
          <Card>
            <CardHeader>
              <CardTitle>Kilometerstand-Uploads</CardTitle>
              <CardDescription>Alle hochgeladenen Tachostände für {format(selectedMonth, "MMMM yyyy", { locale: de })}</CardDescription>
            </CardHeader>
            <CardContent>
              {filteredOdometer.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">Keine Einträge gefunden</p>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Datum</TableHead>
                        <TableHead>Mitarbeiter</TableHead>
                        <TableHead>Fahrzeug</TableHead>
                        <TableHead>Kennzeichen</TableHead>
                        <TableHead className="text-right">km-Stand</TableHead>
                        <TableHead className="text-right">km/Monat</TableHead>
                        <TableHead>Foto</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredOdometer.map((entry) => (
                        <TableRow 
                          key={entry.id}
                          className="cursor-pointer hover:bg-muted/50"
                          onClick={() => navigate(`/employee/${entry.employee_id}`)}
                        >
                          <TableCell>{format(new Date(entry.reading_date), "dd.MM.yyyy")}</TableCell>
                          <TableCell className="font-medium">{entry.employee_name}</TableCell>
                          <TableCell>{entry.vehicle_name}</TableCell>
                          <TableCell>{entry.license_plate}</TableCell>
                          <TableCell className="text-right">{entry.odometer_value.toLocaleString("de-DE")} km</TableCell>
                          <TableCell className="text-right">
                            {entry.kilometers_this_month ? `${entry.kilometers_this_month.toLocaleString("de-DE")} km` : "-"}
                          </TableCell>
                          <TableCell>
                            {entry.photo_url && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setPreviewUrl(entry.photo_url);
                                }}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Photos Tab */}
        <TabsContent value="photos">
          <Card>
            <CardHeader>
              <CardTitle>Fahrzeugfoto-Uploads</CardTitle>
              <CardDescription>Alle hochgeladenen Fahrzeugfotos für {format(selectedMonth, "MMMM yyyy", { locale: de })}</CardDescription>
            </CardHeader>
            <CardContent>
              {filteredPhotos.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">Keine Einträge gefunden</p>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Datum</TableHead>
                        <TableHead>Mitarbeiter</TableHead>
                        <TableHead>Fahrzeug</TableHead>
                        <TableHead>Kennzeichen</TableHead>
                        <TableHead>Typ</TableHead>
                        <TableHead>Foto</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredPhotos.map((entry) => (
                        <TableRow 
                          key={entry.id}
                          className="cursor-pointer hover:bg-muted/50"
                          onClick={() => navigate(`/employee/${entry.employee_id}`)}
                        >
                          <TableCell>{format(new Date(entry.created_at), "dd.MM.yyyy HH:mm")}</TableCell>
                          <TableCell className="font-medium">{entry.employee_name}</TableCell>
                          <TableCell>{entry.vehicle_name}</TableCell>
                          <TableCell>{entry.license_plate}</TableCell>
                          <TableCell>{getPhotoTypeBadge(entry.photo_type)}</TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={(e) => {
                                e.stopPropagation();
                                setPreviewUrl(entry.photo_url);
                              }}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* License Tab */}
        <TabsContent value="license">
          <Card>
            <CardHeader>
              <CardTitle>Führerscheinprüfungen</CardTitle>
              <CardDescription>Alle hochgeladenen Führerscheinnachweise für {format(selectedMonth, "MMMM yyyy", { locale: de })}</CardDescription>
            </CardHeader>
            <CardContent>
              {filteredLicense.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">Keine Einträge gefunden</p>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Datum</TableHead>
                        <TableHead>Mitarbeiter</TableHead>
                        <TableHead>Quartal</TableHead>
                        <TableHead>Jahr</TableHead>
                        <TableHead>Foto</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredLicense.map((entry) => (
                        <TableRow 
                          key={entry.id}
                          className="cursor-pointer hover:bg-muted/50"
                          onClick={() => navigate(`/employee/${entry.employee_id}`)}
                        >
                          <TableCell>{format(new Date(entry.verification_date), "dd.MM.yyyy")}</TableCell>
                          <TableCell className="font-medium">{entry.employee_name}</TableCell>
                          <TableCell>{entry.quarter}</TableCell>
                          <TableCell>{entry.year}</TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={(e) => {
                                e.stopPropagation();
                                setPreviewUrl(entry.photo_url);
                              }}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Preview Dialog */}
      <Dialog open={!!previewUrl} onOpenChange={() => setPreviewUrl(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Bildvorschau</DialogTitle>
          </DialogHeader>
          {previewUrl && (
            <img 
              src={previewUrl} 
              alt="Vorschau" 
              className="w-full h-auto rounded-lg"
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VehicleUploadsOverviewPage;
